import { z } from "zod";

/**
 * Kanonik domain olay kataloğu (CLOS-324 · parent CLOS-280).
 *
 * **Tek kaynak budur.** Üretici de tüketici de olayı buradan tanır; serbest metin olay
 * türü yoktur. Bir olayın adı iki yerde ayrı ayrı yazılabilseydi, gün gelir "aynı iş
 * olayı" için iki ad doğar ve tüketiciler ikiye bölünürdü — biri `contract.signed`,
 * diğeri `contract.imzalandi` dinler ve arada bir olay kaybolur.
 *
 * **Katalogda YALNIZ bugün gerçekten olan durum değişiklikleri var.** Henüz kartı
 * olmayan bir domain için "ileride lazım olur" diye olay tanımlanmaz (ADR-0014); domain
 * kartı geldiğinde kendi satırını ekler ve kilitli ad testini bilerek günceller.
 *
 * Yönlendirme (hangi olay Bildirim/Bekleyen İşlem/Aktivite/Audit üretir) BU DOSYADA
 * DEĞİLDİR — o bir ürün kararıdır ve CLOS-323'ün matrisine aittir. Adlandırma ile
 * yönlendirmeyi aynı yere koymak, teknik bir değişikliği ürün kararına dokunmadan
 * yapmayı imkânsız kılardı.
 */

/** Olayın gerektirdiği aktör türü. **AI yoktur** — AI aktör değil kaynaktır (CLOS-318). */
export type EventActorRequirement = "user" | "system";

/**
 * Payload'ın taşıdığı en hassas veri sınıfı. Saklama ve yönlendirme kararları buna
 * bakar; sınıfı olmayan bir olay, "ne kadar dikkat edileceği" sorusunu cevapsız bırakır.
 */
export type EventSensitivity = "operational" | "personal" | "confidential";

export type EventDefinition = {
  name: string;
  /** Payload şemasının sürümü. Kırıcı değişiklik yeni sürüm ister (bkz. `docs/olay-katalogu.md`). */
  version: number;
  /** Olayı üreten modül — "bu olayı kim değiştirebilir?" sorusunun cevabı. */
  producer: string;
  actor: EventActorRequirement;
  sensitivity: EventSensitivity;
  /** Şema `strict`: bilinmeyen alan reddedilir. */
  payload: z.ZodType;
  summary: string;
};

/**
 * Payload şemaları `tenantId`/`actorId` TANIMLAMAZ — bunlar zarfın alanlarıdır.
 *
 * Payload'da da bulunsalardı iki gerçek kaynağı olurdu ve bir üretici zarftakinden
 * farklı bir tenant yazabilirdi; tüketici hangisine inanacağını bilemezdi.
 */
export const EVENT_CATALOG = {
  "tenant.created": {
    name: "tenant.created",
    version: 1,
    producer: "tenants",
    actor: "user",
    sensitivity: "operational",
    summary: "Yeni firma oluşturuldu.",
    payload: z.strictObject({ name: z.string().min(1) }),
  },
  "membership.created": {
    name: "membership.created",
    version: 1,
    producer: "tenants",
    actor: "user",
    sensitivity: "personal",
    summary: "Firmaya üye eklendi.",
    payload: z.strictObject({
      membershipId: z.uuid(),
      memberUserId: z.uuid(),
      role: z.string().min(1),
    }),
  },
  "file.uploaded": {
    name: "file.uploaded",
    version: 1,
    producer: "files",
    actor: "user",
    sensitivity: "operational",
    // Dosya ADI bilerek YOK: "2026-ihale-teklifi-gizli.pdf" tek başına içerik sızdırır
    // ve olay payload'ı, dosyayı görme yetkisi olmayan tüketicilere de gider.
    summary: "Yükleme doğrulandı; dosya karantinada tarama bekliyor.",
    payload: z.strictObject({
      objectId: z.uuid(),
      domain: z.string().min(1),
      entityId: z.uuid(),
      contentType: z.string().min(1),
      sizeBytes: z.number().int().nonnegative(),
    }),
  },
  "file.scan_clean": {
    name: "file.scan_clean",
    version: 1,
    producer: "files",
    // Tarayıcı sonucunun gerçek kullanıcısı YOKTUR ve uydurulmaz.
    actor: "system",
    sensitivity: "operational",
    summary: "Tarama temiz; dosya teslim edilebilir duruma geçti.",
    payload: z.strictObject({ objectId: z.uuid() }),
  },
  "file.scan_infected": {
    name: "file.scan_infected",
    version: 1,
    producer: "files",
    actor: "system",
    sensitivity: "operational",
    summary: "Tarama zararlı buldu; dosya kalıcı olarak reddedildi.",
    payload: z.strictObject({ objectId: z.uuid(), signature: z.string().min(1) }),
  },
  "file.scan_failed": {
    name: "file.scan_failed",
    version: 1,
    producer: "files",
    actor: "system",
    sensitivity: "operational",
    summary: "Tarayıcıya ulaşılamadı; dosya teslim edilemez, yeniden denenecek.",
    payload: z.strictObject({ objectId: z.uuid(), reason: z.string().min(1) }),
  },
} as const satisfies Record<string, EventDefinition>;

export type DomainEventName = keyof typeof EVENT_CATALOG;

export type EventPayload<N extends DomainEventName> = z.infer<(typeof EVENT_CATALOG)[N]["payload"]>;

/** Ad katalogda var mı — çalışma anında gelen (kuyruk, HTTP) adlar için. */
export function isDomainEventName(value: unknown): value is DomainEventName {
  return typeof value === "string" && value in EVENT_CATALOG;
}
